import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Form, Input, Select, message, Popconfirm, Card, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, TeamOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function WorkGroupManager() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    loadGroups();
    loadUsers();
  }, []);

  const loadGroups = async () => {
    setLoading(true);
    try {
      const resp = await fetch(`${API_URL}/api/work-groups`, {
        headers: { 'x-token': localStorage.getItem('notes_token') }
      });
      const data = await resp.json();
      setGroups(data);
    } catch (e) {
      message.error('加载失败');
    }
    setLoading(false);
  };

  const loadUsers = async () => {
    try {
      const resp = await fetch(`${API_URL}/api/users`, {
        headers: { 'x-token': localStorage.getItem('notes_token') }
      });
      const data = await resp.json();
      if (Array.isArray(data)) {
        setUsers(data);
      } else {
        console.error('用户数据格式错误', data);
        setUsers([]);
      }
    } catch (e) {
      console.error('加载用户失败', e);
      setUsers([]);
    }
  };

  const handleAdd = () => {
    setEditingGroup(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (group) => {
    setEditingGroup(group);
    form.setFieldsValue({ name: group.name, members: group.members });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`${API_URL}/api/work-groups/${id}`, {
        method: 'DELETE',
        headers: { 'x-token': localStorage.getItem('notes_token') }
      });
      message.success('删除成功');
      loadGroups();
    } catch (e) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const url = editingGroup 
        ? `${API_URL}/api/work-groups/${editingGroup.id}`
        : `${API_URL}/api/work-groups`;
      const method = editingGroup ? 'PUT' : 'POST';
      
      await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'x-token': localStorage.getItem('notes_token')
        },
        body: JSON.stringify(values)
      });
      
      message.success(editingGroup ? '更新成功' : '创建成功');
      setModalVisible(false);
      loadGroups();
    } catch (e) {
      message.error('操作失败');
    }
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/work-logs')} style={{ marginRight: 16 }}>
          返回
        </Button>
        <h2 style={{ margin: 0 }}><TeamOutlined /> 项目组管理</h2>
        <div style={{ marginLeft: 'auto' }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            新建项目组
          </Button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
        {groups.map(group => (
          <Card
            key={group.id}
            title={group.name}
            extra={
              <span>
                <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(group)} />
                <Popconfirm title="确定删除？" onConfirm={() => handleDelete(group.id)}>
                  <Button type="link" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </span>
            }
          >
            <div style={{ marginBottom: 8, color: '#666' }}>成员：</div>
            <div>
              {group.members.map(m => {
                const user = users.find(u => u.username === m);
                const displayName = user?.realname || m;
                return (
                  <Tag key={m} color="blue">{displayName}</Tag>
                );
              })}
            </div>
          </Card>
        ))}
      </div>

      {groups.length === 0 && !loading && (
        <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>
          暂无项目组，点击右上角创建
        </div>
      )}

      <Modal
        title={editingGroup ? '编辑项目组' : '新建项目组'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="项目名称" rules={[{ required: true, message: '请输入项目名称' }]}>
            <Input placeholder="请输入项目名称" />
          </Form.Item>
          <Form.Item name="members" label="项目成员" rules={[{ required: true, message: '请选择成员' }]}>
            <Select
              mode="multiple"
              placeholder="请选择成员"
              showSearch
              optionFilterProp="label"
              style={{ width: '100%' }}
            >
              {users.map(u => (
                <Select.Option key={u.username} value={u.username} label={u.realname || u.username}>
                  {u.realname ? `${u.realname} (${u.username})` : u.username}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
